import type { FormEvent } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, Github, KeyRound, Link2, UserRound } from 'lucide-react'
import { DetailHeader, DetailPage } from '@/components/detail-page'
import { ApiKeyList } from '@/components/settings/ApiKeyList'
import { WorkspaceAsideCard, WorkspacePageGrid, WorkspaceSection } from '@/components/workspace-form'
import { Button } from '@/components/ui/button'
import { FormMessage } from '@/components/ui/form-message'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api-client'
import type { Connection } from '@/lib/api-client'

export default function SettingsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isUpdating, setIsUpdating] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{ name?: string }>({})
  const [connections, setConnections] = useState<Connection[]>([])
  const [isLoadingConnections, setIsLoadingConnections] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  })

  const loadConnections = useCallback(async () => {
    setIsLoadingConnections(true)
    try {
      const response = await apiClient.listConnections()
      if (response.success && response.data) {
        setConnections(response.data.connections || [])
      }
    } catch {
      // Silently fail — connections list is non-critical
    } finally {
      setIsLoadingConnections(false)
    }
  }, [])

  useEffect(() => {
    loadConnections()
  }, [loadConnections])

  const handleConnectGitHub = () => {
    localStorage.setItem('auth_return_url', '/settings')
    window.location.href = apiClient.getConnectGitHubUrl()
  }

  const handleDisconnectGitHub = async () => {
    setIsDisconnecting(true)
    try {
      const response = await apiClient.disconnectGitHub()
      if (response.success) {
        toast({ title: 'Disconnected', description: 'GitHub account has been disconnected.', variant: 'success' })
        setConnections(connections.filter(c => c.provider !== 'github'))
      } else {
        throw new Error(response.error || 'Failed to disconnect')
      }
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to disconnect', variant: 'destructive' })
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleProfileUpdate = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = profileData.name.trim()

    if (!trimmedName) {
      setFieldErrors({
        name: 'Enter the name that should appear on your workspace profile.',
      })
      return
    }

    setIsUpdating(true)

    try {
      const response = await apiClient.updateProfile({
        name: trimmedName,
        email: profileData.email,
      })

      if (response.success) {
        toast({
          title: 'Profile updated',
          description: 'Workspace profile details were updated successfully.',
          variant: 'success',
        })
      } else {
        throw new Error(response.error || 'Failed to update profile')
      }
    } catch (updateError) {
      toast({
        title: 'Error',
        description: updateError instanceof Error ? updateError.message : 'Failed to update profile',
        variant: 'destructive',
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <DetailPage className="max-w-6xl">
      <DetailHeader
        title="Settings"
        description="Manage your profile, API keys, and plan information."
        breadcrumbs={(
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Settings
          </span>
        )}
      />

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 rounded-[20px] border border-border/70 bg-background/70 p-1 sm:w-auto sm:min-w-[36rem]">
          <TabsTrigger value="profile" className="gap-2">
            <UserRound className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="connections" className="gap-2">
            <Link2 className="h-4 w-4" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="gap-2">
            <KeyRound className="h-4 w-4" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="billing" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <WorkspacePageGrid
            aside={(
              <WorkspaceAsideCard
                title="Current plan"
                tone="accent"
                description={`${user?.plan || 'Free'} plan`}
              />
            )}
            main={(
              <WorkspaceSection
                title="Profile"
                description="Update the name shown in EtherPilot. Email is managed by your sign-in provider."
              >
                <form onSubmit={handleProfileUpdate} className="space-y-5">
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        type="text"
                        value={profileData.name}
                        onChange={(event) => {
                          setProfileData({
                            ...profileData,
                            name: event.target.value,
                          })
                          if (fieldErrors.name) {
                            setFieldErrors({ name: undefined })
                          }
                        }}
                        placeholder="Enter your name"
                        aria-invalid={Boolean(fieldErrors.name)}
                      />
                      {fieldErrors.name ? (
                        <FormMessage variant="error">{fieldErrors.name}</FormMessage>
                      ) : (
                        <FormMessage>This name appears in EtherPilot account views.</FormMessage>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(event) =>
                          setProfileData({
                            ...profileData,
                            email: event.target.value,
                          })}
                        placeholder="Enter your email"
                        disabled
                      />
                      <FormMessage>Email changes are managed by your sign-in provider.</FormMessage>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isUpdating}>
                      {isUpdating ? 'Updating...' : 'Update profile'}
                    </Button>
                  </div>
                </form>
            </WorkspaceSection>
          )}
          />
        </TabsContent>

        <TabsContent value="connections">
          <WorkspacePageGrid
            aside={(
              <WorkspaceAsideCard
                title="External accounts"
                description="Connect external services to use their features within EtherPilot, such as browsing GitHub repositories."
              />
            )}
            main={(
              <WorkspaceSection
                title="Connected accounts"
                description="Link external service accounts to enable integrations like GitHub repository browsing."
              >
                {isLoadingConnections ? (
                  <div className="text-sm text-muted-foreground">Loading connections...</div>
                ) : (
                  <div className="space-y-4">
                    {/* GitHub connection */}
                    {(() => {
                      const githubConn = connections.find(c => c.provider === 'github')
                      if (githubConn) {
                        return (
                          <div className="flex items-center justify-between rounded-lg border p-4">
                            <div className="flex items-center gap-3">
                              <Github className="h-5 w-5" />
                              <div>
                                <p className="text-sm font-medium">GitHub</p>
                                <p className="text-xs text-muted-foreground">
                                  Connected as <span className="font-mono">@{githubConn.login}</span>
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleDisconnectGitHub}
                              disabled={isDisconnecting}
                            >
                              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                            </Button>
                          </div>
                        )
                      }
                      return (
                        <div className="flex items-center justify-between rounded-lg border border-dashed p-4">
                          <div className="flex items-center gap-3">
                            <Github className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">GitHub</p>
                              <p className="text-xs text-muted-foreground">
                                Connect to browse and add GitHub repositories as networks.
                              </p>
                            </div>
                          </div>
                          <Button size="sm" onClick={handleConnectGitHub}>
                            Connect
                          </Button>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </WorkspaceSection>
            )}
          />
        </TabsContent>

        <TabsContent value="api-keys">
          <WorkspacePageGrid
            aside={(
              <WorkspaceAsideCard
                title="API access"
                description="Create and revoke personal API keys for automation and external integrations."
              />
            )}
            main={(
              <WorkspaceSection
                title="API keys"
                description="Manage API keys used by CI and other automation."
              >
                <ApiKeyList />
              </WorkspaceSection>
            )}
          />
        </TabsContent>

        <TabsContent value="billing">
          <WorkspacePageGrid
            aside={(
              <WorkspaceAsideCard
                title="Current plan"
                description={`${user?.plan || 'Free'} plan`}
              />
            )}
            main={(
              <WorkspaceSection
                title="Billing"
                description="Review your current plan and compare plans when you need to change it."
              >
                <WorkspaceAsideCard
                  title="Plan"
                  description={`Your account is on the ${user?.plan || 'Free'} plan.`}
                >
                  <Button variant="outline" onClick={() => navigate('/pricing')}>
                    View plans
                  </Button>
                </WorkspaceAsideCard>
              </WorkspaceSection>
            )}
          />
        </TabsContent>
      </Tabs>
    </DetailPage>
  )
}
